import { Switch, Route, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import { Layout } from "@/components/Layout";
import NotFound from "@/pages/not-found";
import { townSlugs, blogSlugs } from "@/seo/slugs";
import { categoryPages } from "@/data/categories";
import { AUTHOR_PAGE_PATH } from "@/seo/jsonld";
import { redirects } from "@/seo/redirects";
import { trackPageView, trackEvent } from "@/lib/analytics";

// Home is the landing / LCP page, so keep it eager. Every other route is
// lazy-loaded so it is split out of the initial bundle and only fetched
// when the user navigates there (reduces initial JS and main-thread work).
import Home from "@/pages/Home";
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const AIFactsPage = lazy(() => import("@/pages/AIFactsPage"));
const Services = lazy(() => import("@/pages/Services"));
const Photos = lazy(() => import("@/pages/Photos"));
const Testimonials = lazy(() => import("@/pages/Testimonials"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const AreasServed = lazy(() => import("@/pages/AreasServed"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));

const RoofingHub = lazy(() => import("@/pages/RoofingHub"));
const RoofRepair = lazy(() => import("@/pages/RoofRepair"));
const RoofRejuvenation = lazy(() => import("@/pages/RoofRejuvenation"));
const RoofInspection = lazy(() => import("@/pages/RoofInspection"));
const FreeEstimates = lazy(() => import("@/pages/FreeEstimates"));
const MetalRoofing = lazy(() => import("@/pages/MetalRoofing"));
const RubberRoofing = lazy(() => import("@/pages/RubberRoofing"));
const ShingleRoofing = lazy(() => import("@/pages/ShingleRoofing"));
const StormDamage = lazy(() => import("@/pages/StormDamage"));
const LancasterCountyContractor = lazy(() => import("@/pages/LancasterCountyContractor"));

const SidingHub = lazy(() => import("@/pages/SidingHub"));
const GuttersPage = lazy(() => import("@/pages/GuttersPage"));
const RoofingCostEstimator = lazy(() => import("@/pages/RoofingCostEstimator"));

const BlogPost = lazy(() => import("@/pages/BlogPost"));

const GeoRoofing = lazy(() => import("@/pages/geo/GeoRoofing"));
const GeoSiding = lazy(() => import("@/pages/geo/GeoSiding"));
const GeoGutters = lazy(() => import("@/pages/geo/GeoGutters"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function Analytics() {
  const [location] = useLocation();

  // Send a GA4 page_view on the initial load and on every client-side route
  // change (gtag is configured with send_page_view: false so we control this).
  useEffect(() => {
    trackPageView(window.location.pathname + window.location.search);
  }, [location]);

  // Central click-to-call tracking: one delegated listener captures every
  // tel: link on the site (header, footer, sticky button, inline copy,
  // article links) without touching each component.
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!link) return;
      trackEvent("phone_call", {
        event_category: "engagement",
        event_label: link.getAttribute("href") ?? "",
        link_text: link.textContent?.trim() ?? "",
      });
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return null;
}

function Router() {
  return (
    <Switch>
      {/* Homepage */}
      <Route path="/" component={Home} />

      {/* Contact & About */}
      <Route path="/contact-graystone-roofing-siding/" component={Contact} />
      <Route path="/about-graystone-roofing-siding-lancaster/" component={About} />
      <Route path="/about-graystone-roofing-siding-lancaster/graystone-roofing-siding-company-facts/" component={AIFactsPage} />

      {/* Services overview */}
      <Route path="/services/" component={Services} />

      {/* Roofing hub & sub-pages */}
      <Route path="/services/roofing-company-lancaster-pa/" component={RoofingHub} />
      <Route path="/services/roof-repair-lancaster-pa/" component={RoofRepair} />
      <Route path="/services/roof-rejuvenation-lancaster-pa/" component={RoofRejuvenation} />
      <Route path="/services/roofing-inspection-lancaster-pa/" component={RoofInspection} />
      <Route path="/services/free-roof-estimates-lancaster-pa/" component={FreeEstimates} />
      <Route path="/services/metal-roofing-lancaster-pa/" component={MetalRoofing} />
      <Route path="/services/rubber-roofing-installation-lancaster-pa/" component={RubberRoofing} />
      <Route path="/services/shingle-roofing-lancaster-pa/" component={ShingleRoofing} />
      <Route path="/services/roofing-storm-damage-lancaster-pa/" component={StormDamage} />
      <Route path="/service-areas/roofing-contractor-lancaster-county-pa/" component={LancasterCountyContractor} />
      <Route path="/service-areas/" component={AreasServed} />

      {/* Siding & Gutters */}
      <Route path="/services/siding-contractor-lancaster-pa/" component={SidingHub} />
      <Route path="/services/gutter-installation-lancaster-pa-york-pa/" component={GuttersPage} />

      {/* Photo gallery, testimonials */}
      <Route path="/roofing-siding-photos-lancaster-pa/" component={Photos} />
      <Route path="/roofing-siding-york-lancaster-pa-testimonials/" component={Testimonials} />

      {/* Cost calculator */}
      <Route path="/roofing-siding-cost-calculator-lancaster-pa/" component={RoofingCostEstimator} />

      {/* Blog */}
      <Route path="/blog/" component={BlogIndex} />

      {/* Category hub pages — one per Help Hub category */}
      {categoryPages.map(c => (
        <Route
          key={c.slug}
          path={`/category/${c.slug}/`}
          component={() => <CategoryPage slug={c.slug} />}
        />
      ))}

      {/* Author / E-E-A-T profile page */}
      <Route path={AUTHOR_PAGE_PATH} component={AuthorPage} />


      {/* Help Hub article routes (derived from blogPosts) */}
      {blogSlugs.map(slug => (
        <Route key={slug} path={`/${slug}/`} component={() => <BlogPost slug={slug} />} />
      ))}

      {/* Geo roofing pages — 40 towns */}
      {townSlugs.map(slug => (
        <Route
          key={`roofing-${slug}`}
          path={`/service-areas/roofing-contractor-${slug}-pa/`}
          component={() => <GeoRoofing slug={slug} />}
        />
      ))}

      {/* Geo siding pages — 40 towns */}
      {townSlugs.map(slug => (
        <Route
          key={`siding-${slug}`}
          path={`/service-areas/siding-contractor-${slug}-pa/`}
          component={() => <GeoSiding slug={slug} />}
        />
      ))}

      {/* Geo gutter pages — 40 towns */}
      {townSlugs.map(slug => (
        <Route
          key={`gutters-${slug}`}
          path={`/service-areas/gutter-installation-${slug}-pa/`}
          component={() => <GeoGutters slug={slug} />}
        />
      ))}

      {/* Permanently moved URLs: client-side redirects (see src/seo/redirects.ts).
          Static hosting serves prerendered redirect stubs for direct hits. */}
      {redirects.map(({ from, to }) => (
        <Route key={`redirect-${from}`} path={from} component={() => <Redirect to={to} replace />} />
      ))}

      {/* 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <ScrollToTop />
            <Analytics />
            <Layout>
              <Suspense fallback={<div className="min-h-screen" />}>
                <Router />
              </Suspense>
            </Layout>
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
